import type { SegmentAggregate, EvidenceCitation, ValidationCell } from '../types'
import { ALL_PASSES, runPass } from './passes'
import { selectPassesForSegment } from './selection'
import { aggregatePassResults } from './aggregator'
import { buildValidationPlan, simulateValidation } from './validation'
import { DEMO_SEGMENTS, DEMO_STIMULI, DEMO_EVIDENCE } from '../demo/data'

export interface DemoRunResult {
  aggregates: SegmentAggregate[]
  validation_plan: ValidationCell[]
  simulated_validation: ValidationCell[]
  passes_used: string[]
}

export async function runDemoEnsemble(
  researchQuestion: string,
  selectedSegmentIds: string[]
): Promise<DemoRunResult> {
  const aggregates: SegmentAggregate[] = []

  const segments = DEMO_SEGMENTS.filter(s =>
    selectedSegmentIds.length === 0 || selectedSegmentIds.includes(s.segment_id)
  )

  for (const segment of segments) {
    for (const stimulus of DEMO_STIMULI) {
      const selectedPasses = selectPassesForSegment(segment.name)
      const evidenceRows = DEMO_EVIDENCE.filter(e =>
        !e.segment || e.segment.toLowerCase().includes(segment.name.toLowerCase().split(' ')[0])
      )

      const passResults = selectedPasses.map(pass =>
        runPass(pass, {
          research_question: researchQuestion,
          stimuli: DEMO_STIMULI,
          segment_id: segment.segment_id,
          segment_name: segment.name,
          evidence_rows: evidenceRows,
          ground_truth_present: false,
          is_demo: true,
        })
      )

      const citations: EvidenceCitation[] = evidenceRows.slice(0, 3).map((row, i) => ({
        source_file: row.source_file,
        question: 'renewal_preference_open',
        segment: segment.name,
        quote: row.open_text?.slice(0, 180) ?? 'No open-end response',
        evidence_strength: i === 0 ? 'High' : 'Medium',
        row_index: row.row_index,
      }))

      const isClaimant = segment.name.toLowerCase().includes('claim')
      const segmentCoverage = isClaimant ? 0.28 : 0.72
      const openEndedDepth = isClaimant ? 0.35 : 0.68

      const agg = aggregatePassResults(
        passResults,
        segment.segment_id,
        segment.name,
        stimulus.stimulus_id,
        stimulus.label,
        evidenceRows.length,
        openEndedDepth,
        segmentCoverage,
        false,
        citations
      )

      aggregates.push(agg)
    }
  }

  const validation_plan = buildValidationPlan(aggregates)
  const simulated_validation = simulateValidation(validation_plan)

  return {
    aggregates,
    validation_plan,
    simulated_validation,
    passes_used: ALL_PASSES.map(p => p.template),
  }
}
